/**
 * Manages the storage of web push notification messages.
 *
 * Also updates the app badge count.
 */
import { signal } from "@preact/signals";

import readlangClient from "readlangClient";

export interface Notification {
  _id: string;
  title: string;
  body: string;
}

export default class StoredNotificationsStore {
  readonly notifications = signal<Notification[]>([]);

  private midnightTimerId: ReturnType<typeof setTimeout> | undefined =
    undefined;
  private visibilityChangeListener: (() => void) | undefined;
  private serviceWorkerListener: ((event: MessageEvent) => void) | undefined;
  private setupComplete = false;
  private fetchInFlight: Promise<void> | undefined;

  get hasNotifications(): boolean {
    return this.notifications.value.length > 0;
  }

  fetchAndSetup() {
    void this.fetchNotificationsAndUpdateBadgeCount();

    if (this.setupComplete) {
      return;
    }
    this.setupComplete = true;

    // Re-fetch when the user returns to the tab
    this.visibilityChangeListener = () => {
      if (window.document.visibilityState === "visible") {
        void this.fetchNotificationsAndUpdateBadgeCount();
      }
    };
    document.addEventListener(
      "visibilitychange",
      this.visibilityChangeListener
    );

    // Listen for messages from the service worker
    this.serviceWorkerListener = (event: MessageEvent) => {
      if (event.data.type === "PUSH_NOTIFICATION_RECEIVED") {
        void this.fetchNotificationsAndUpdateBadgeCount();
      } else if (event.data.type === "NOTIFICATION_CLICKED") {
        if (event.data.path && event.data.path !== window.location.pathname) {
          window.location.href = event.data.path;
        }
      }
    };
    navigator.serviceWorker?.addEventListener(
      "message",
      this.serviceWorkerListener
    );
  }

  private updateMidnightTimer() {
    if (this.midnightTimerId) {
      clearTimeout(this.midnightTimerId);
    }

    // Re-fetch stored notifications at midnight local time since that's when they expire and we'll
    // want to reset the badge count.
    const now = new Date();
    const midnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      0,
      0,
      0
    );
    const timeUntilMidnight = midnight.getTime() - now.getTime();

    this.midnightTimerId = setTimeout(
      () => void this.fetchNotificationsAndUpdateBadgeCount(),
      timeUntilMidnight
    );
  }

  private async fetchNotificationsAndUpdateBadgeCount() {
    if (this.fetchInFlight) {
      return this.fetchInFlight;
    }

    this.fetchInFlight = this.fetchNotificationsAndUpdateBadgeCountImpl();
    try {
      await this.fetchInFlight;
    } finally {
      this.fetchInFlight = undefined;
    }
  }

  private async fetchNotificationsAndUpdateBadgeCountImpl() {
    const notifications = await new Promise<Notification[]>((resolve) => {
      readlangClient.request(
        "GET",
        "/api/webPushNotifications",
        {},
        (data: Notification[]) => resolve(data),
        (error: any) => {
          if (error?.status !== 429) {
            console.warn("Failed to fetch stored notifications", error);
          }
          resolve(this.notifications.value);
        }
      );
    });

    navigator.setAppBadge?.(notifications.length > 0 ? 1 : 0);

    this.notifications.value = notifications;

    this.updateMidnightTimer();
  }

  async deleteAllStoredNotifications() {
    // Update local state directly rather than refetching afterwards: the GET
    // endpoint is rate limited, so a refetch straight after the page-load
    // fetch would 429 and leave the deleted notifications visible.
    try {
      for (const notification of this.notifications.value) {
        await readlangClient.request(
          "DELETE",
          `/api/webPushNotifications/${notification._id}`,
          {}
        );
        this.notifications.value = this.notifications.value.filter(
          (remaining) => remaining._id !== notification._id
        );
      }
    } catch (error) {
      // A mid-loop failure (readlangClient has already notified if terminal)
      // keeps the remaining notifications; don't leave callers hanging on a
      // rejection — e.g. the streak dialog would never close.
      console.error("Error deleting stored notifications:", error);
    } finally {
      navigator.setAppBadge?.(this.notifications.value.length > 0 ? 1 : 0);
    }
  }

  dispose() {
    if (this.midnightTimerId) {
      clearTimeout(this.midnightTimerId);
      this.midnightTimerId = undefined;
    }
    if (this.visibilityChangeListener) {
      document.removeEventListener(
        "visibilitychange",
        this.visibilityChangeListener
      );
      this.visibilityChangeListener = undefined;
    }
    if (this.serviceWorkerListener) {
      navigator.serviceWorker?.removeEventListener(
        "message",
        this.serviceWorkerListener
      );
      this.serviceWorkerListener = undefined;
    }
  }
}
